import React, { useState, useEffect, useRef, HtmlHTMLAttributes, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import styles from './index.module.scss';
import { twMerge } from 'tailwind-merge';
import { WeeeIcon } from '../Icon';
import { fixedBody, looseBody } from '@/utils/scroll';
import Button from '../Button';
import { PopupProps } from '@/components/common/Popup';

type DialogProps = Partial<PopupProps> & {
  content: React.ReactNode | string;
} & Record<string, any>;

const PopupCom = forwardRef(
  ({
    show = false,
    onClose = () => {},
    afterClose = () => {},
    animationType = 'scale-in',
    zIndex = 1000,
    destroyOnClose = false,
    position = 'center',
    title = '',
    children,
    animateTime = 250,
    closable = false,
    popupId = 'popup',
    className = '',
    wrapperClassName = '',
    wrapperStyle = {},
    headerClassName = '',
    maskClassName = '',
    popStyle = {},
    maskClosable = true,
    isFixedBody = true
  }: PopupProps) => {
    const [firstRendered, setFirstRendered] = useState<boolean>(false);
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      let timer: any;
      if (show) {
        const otherOpenPopup = document.querySelector(`[data-popup-visible="true"]:not(#${popupId})`);
        !otherOpenPopup && isFixedBody && fixedBody();
        setFirstRendered(true);
      } else {
        timer = setTimeout(() => {
          const otherOpenPopup = document.querySelector(`[data-popup-visible="true"]:not(#${popupId})`);
          if (!otherOpenPopup && isFixedBody) {
            looseBody();
          }
        }, animateTime);
      }
      return () => {
        clearTimeout(timer);
      };
    }, [show, animateTime, isFixedBody, popupId]);

    // Ensure body is loosened when component unmounts
    useEffect(() => {
      return () => {
        const otherOpenPopup = document.querySelector(`[data-popup-visible="true"]:not(#${popupId})`);
        if (!otherOpenPopup && isFixedBody) {
          looseBody();
        }
      };
    }, [isFixedBody, popupId]);

    useEffect(() => {
      if (!show && destroyOnClose && popupRef.current) {
        const timer = setTimeout(() => {
          afterClose();
          firstRendered && setFirstRendered(false);
          clearTimeout(timer);
          popupRef.current?.remove?.();
        }, Math.max(animateTime, 300));
      }
    }, [show, destroyOnClose, afterClose, animateTime]);

    if (!firstRendered && !show) {
      return null;
    }

    return (
      <div
        data-type="popup"
        data-popup-visible={show}
        id={popupId}
        style={{ zIndex, animationDuration: `${animateTime}ms`, transform: 'translateZ(5px)', ...popStyle }}
        ref={popupRef}
        className={twMerge(
          `fixed inset-0 flex items-center justify-center ${!show ? styles['zHide'] : ''}`,
          className
        )}>
        <div
          className={twMerge(
            `touch-none absolute inset-0 bg-reserved-true-black ${show ? styles['fade-in'] : styles['fade-out']}`,
            maskClassName
          )}
          style={{ animationDuration: `${animateTime}ms` }}
          onClick={e => {
            e.stopPropagation();
            if (maskClosable) {
              onClose(e);
            }
          }}
        />
        <div
          style={{ animationDuration: `${animateTime}ms`, ...wrapperStyle }}
          className={twMerge(
            `${popupId}-content`,
            `touch-none bg-surface-100-bg max-w-[750px] mx-auto box-border inset-x-0 transform ${
              show ? styles[animationType + '-enter'] : styles[animationType + '-leave']
            } absolute`,
            position === 'top' && 'top-0',
            position === 'bottom' && 'bottom-0 inset-x-0 pb-safe',
            position === 'left' && 'left-0 inset-y-0',
            position === 'right' && 'right-0',
            position === 'center' && 'relative',
            wrapperClassName
          )}>
          {(title || closable) && (
            <header
              id={`${popupId}-header`}
              className={twMerge('p-5 relative flex justify-center items-center', headerClassName)}>
              {title && typeof title === 'string' ? <h2 className="enki-body-base-medium">{title}</h2> : title}
              {closable && (
                <div
                  className="absolute left-5 top-1/2 -translate-y-1/2"
                  onClick={() => {
                    onClose();
                  }}>
                  <WeeeIcon type="iconTimes" className=" text-surface-100-fg" style={{ fontSize: 36 }} />
                </div>
              )}
            </header>
          )}
          {children}
        </div>
      </div>
    );
  }
);
const Popup = forwardRef((props: PopupProps, ref) => {
  const [show, setShow] = useState<boolean>(props.show);
  useImperativeHandle(ref, () => ({
    show(visible: boolean) {
      setShow(visible);
    }
  }));
  return <PopupCom {...props} show={show} ref={ref} />;
}) as typeof PopupCom;

const createDialog = (
  props: DialogProps & {
    [key: string]: any;
  }
) => {
  const div = document.createElement('div');
  const popupId = `popup-${Math.random().toString(36).substr(2)}`;
  div.setAttribute('data-popupId', popupId);
  const dialogRef = React.createRef<any>();
  document.body.appendChild(div);

  ReactDOM.render(
    <Popup
      destroyOnClose
      popupId={popupId}
      show={true}
      ref={dialogRef}
      onClose={() => {
        props.onClose?.();
        dialogRef.current.show(false);
      }}
      afterClose={() => {
        props.afterClose?.();
        ReactDOM.unmountComponentAtNode(div);
        div.remove();
      }}
      {...props}>
      {props.content}
    </Popup>,
    div
  );

  return {
    close: () => {
      dialogRef.current.show(false);
    },
    ref: dialogRef
  };
};
const Dialog = (props: DialogProps) => {
  return createDialog(props);
};

Dialog.confirm = (
  props: DialogProps & {
    handleOk?: () => void;
    handleCancel?: () => void;
    okText?: string;
    cancelText?: string;
    showCancel?: boolean;
    cancelClassName?: string;
    okClassName?: string;
    /** 内容区域的类名 */
    contentClassName?: string;
    /** 点击确定后是否关闭弹窗 */
    closeOnOk?: boolean;
    /** 按钮组的类名 */
    btnBoxClassName?: string;
  }
) => {
  const dialog = createDialog({
    wrapperClassName: 'w-full mx-5 rounded-500',
    position: 'center',
    animationType: 'scale-in',
    maskClosable: false,
    closeOnOk: true,
    animateTime: 150,
    ...props,
    content: (
      <div className={twMerge('px-9 pb-6 pt-9 text-center', props.contentClassName)}>
        {props.content}
        <div className={twMerge('px-5', props.btnBoxClassName)}>
          <Button
            type="primary"
            className={twMerge(props.okClassName)}
            onClick={() => {
              props.handleOk?.();
              props?.closeOnOk && dialog.close();
            }}>
            {props.okText}
          </Button>
          {props.showCancel && (
            <Button
              className={twMerge(props.cancelClassName)}
              onClick={() => {
                props.handleCancel?.();
                dialog.close();
              }}>
              {props.cancelText}
            </Button>
          )}
        </div>
      </div>
    )
  });
  return dialog;
};
export default Dialog;
